/* @flow */
import cx from 'classnames';
import * as React from 'react';

import { ReactionContainer } from 'chat/components/Reaction';
import AnnouncementMessageItemHeader from './AnnouncementMessageItemHeader';
import Avatar from 'components/Avatar';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import GroupAvatar from 'groups/components/GroupAvatar';
import MessageItemBody from './MessageItemBody';
import ReactionDisplay from 'chat/components/ReactionDisplay';
import ReactionStatus from 'chat/components/ReactionStatus';
import Time from 'components/Time';
import UserAvatar from 'users/components/UserAvatar';
import UrgentAvatar from 'chat/components/UrgentAvatar';
import MessageBodyTranslationSwitcher from './MessageBodyTranslationSwitcher';

import './styles/AnnouncementMessageItem.scss';

type Props = {
  item: *,
  streamUuid: string,
  isLastItemOfChunk: boolean,
  canViewDeliveryInformation: boolean,
};

type State = {
  reactionPickerVisible: boolean,
  hovered: boolean,
  showOriginal: boolean,
};

class AnnouncementMessageItem extends React.Component<Props, State> {
  state: State = {
    reactionPickerVisible: false,
    hovered: false,
    showOriginal: false,
  };

  handleEnterHover = () => this.setState({ hovered: true });
  handleLeaveHover = () => this.setState({ hovered: false });

  isAuthor() {
    return CurrentUser.getUserUuid() === this.props.item.item.sender.uuid;
  }

  renderDroplet() {
    const { isLastItemOfChunk } = this.props;

    if (!isLastItemOfChunk || this.isAuthor()) {
      return null;
    }

    return (
      <span className="AnnouncementMessageItem-droplet">
        <UserAvatar user={this.props.item.item.sender} size="small" />
      </span>
    );
  }

  renderAnnouncementHeader() {
    const { streamUuid, canViewDeliveryInformation } = this.props;
    const { group, sender, target, uuid, reactionSummary, urgent } = this.props.item.item;

    const headerComponent = (avatar: *, targetTitle: string, targetDescription?: ?string) => {
      if (urgent) {
        return (
          <AnnouncementMessageItemHeader
            avatar={
              <UrgentAvatar size="small" className="AnnouncementMessageItem-announcementBadge" />
            }
            targetTitle={targetTitle}
            targetDescription={targetDescription}
            canViewDeliveryInformation={canViewDeliveryInformation}
            streamUuid={streamUuid}
            messageUuid={uuid}
            reactions={reactionSummary ? reactionSummary.reactions : null}
            urgent
          />
        );
      }
      return (
        <AnnouncementMessageItemHeader
          avatar={avatar}
          targetTitle={targetTitle}
          targetDescription={targetDescription}
          canViewDeliveryInformation={canViewDeliveryInformation}
          streamUuid={streamUuid}
          messageUuid={uuid}
          reactions={reactionSummary ? reactionSummary.reactions : null}
        />
      );
    };

    if (target) {
      const avatar = (
        <Avatar image={target.iconUrls[0]} className="AnnouncementMessageItem-announcementBadge" />
      );
      return headerComponent(avatar, target.title, target.description);
    }

    if (group) {
      const avatar = (
        <GroupAvatar className="AnnouncementMessageItem-announcementBadge" group={group} />
      );
      const targetText = group.class_name;
      return headerComponent(avatar, targetText);
    }

    if (sender && sender.__typename === 'EntitySender') {
      const avatar = (
        <GroupAvatar
          className="AnnouncementMessageItem-announcementBadge"
          group={{ groupAvatar: { svg_url: sender.avatarUrl } }}
        />
      );
      const targetText = sender.name;
      return headerComponent(avatar, targetText);
    }

    return (
      <ErrorReporter error="AnnouncementMessageItem - tried to render item header without target, group, or sender" />
    );
  }

  renderReactionStatus() {
    const messageUuid = this.props.item.item.uuid;
    const { streamUuid } = this.props;

    if (!streamUuid) {
      return (
        <ErrorReporter error="AnnouncementMessageItem - tried to render reaction status without streamUuid" />
      );
    } else if (!messageUuid) {
      return (
        <ErrorReporter error="AnnouncementMessageItem - tried to render reaction status without messageUuid" />
      );
    }

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={messageUuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => (
          <ReactionStatus
            sentBySelf={this.isAuthor()}
            reactionSummary={reactionSummary}
            addReactionToSummary={addReactionToSummary}
            removeReactionFromSummary={removeReactionFromSummary}
          />
        )}
      </ReactionContainer>
    );
  }

  renderReactions() {
    const messageUuid = this.props.item.item.uuid;
    const { streamUuid } = this.props;

    if (!streamUuid) {
      return (
        <ErrorReporter error="AnnouncementMessageItem - tried to render reactions without streamUuid" />
      );
    } else if (!messageUuid) {
      return (
        <ErrorReporter error="AnnouncementMessageItem - tried to render reactions without messageUuid" />
      );
    }

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={messageUuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => (
          <ReactionDisplay
            sentBySelf={this.isAuthor()}
            reactionSummary={reactionSummary}
            addReactionToSummary={addReactionToSummary}
            removeReactionFromSummary={removeReactionFromSummary}
          />
        )}
      </ReactionContainer>
    );
  }

  hasTranslations() {
    return !this.isAuthor() && this.props.item.item.translationDetails;
  }

  originalLanguageCode() {
    const { translationDetails } = this.props.item.item;
    if (!translationDetails) {
      return null;
    }

    return translationDetails.originalLanguageCode;
  }

  toggleOriginal = () => {
    this.setState({ showOriginal: !this.state.showOriginal });
  };

  renderTranslationSwitcher() {
    const { showOriginal } = this.state;

    return (
      <MessageBodyTranslationSwitcher
        showOriginal={showOriginal}
        originalLanguageCode={this.originalLanguageCode()}
        onToggleShowOriginal={this.toggleOriginal}
      />
    );
  }

  renderHover() {
    const { item } = this.props.item;
    const date = item.sentAt || item.createdAt;

    return date ? (
      <div className="AnnouncementMessageItem-hover">
        <div className="MessageItem-hover-receipt">
          <Time date={new Date(date)} hour="numeric" minute="2-digit" />
        </div>
      </div>
    ) : null;
  }

  render() {
    const { item } = this.props.item;
    const { hovered, reactionPickerVisible } = this.state;

    return (
      <div
        className={cx('AnnouncementMessageItem', {
          'AnnouncementMessageItem-self': this.isAuthor(),
          'AnnouncementMessageItem-other': !this.isAuthor(),
          'AnnouncementMessageItem-hovered': hovered || reactionPickerVisible,
        })}>
        <div
          className="AnnouncementMessageItem-row"
          onMouseEnter={this.handleEnterHover}
          onMouseLeave={this.handleLeaveHover}>
          {this.renderHover()}
          <div className="AnnouncementMessageItem-column">
            <div className="AnnouncementMessageItem-dropletChubble">
              <div className="AnnouncementMessageItem-chubble">
                {this.renderAnnouncementHeader()}
                <div className="AnnouncementMessageItem-content">
                  <MessageItemBody item={item} showOriginal={this.state.showOriginal} />
                </div>
              </div>
              {this.renderDroplet()}
            </div>
            {this.hasTranslations() && this.renderTranslationSwitcher()}
            {item.canReact && this.renderReactionStatus()}
          </div>
        </div>
        {item.canReact && this.renderReactions()}
      </div>
    );
  }
}

export default AnnouncementMessageItem;
